import { createContext, use, useMemo, useCallback, ReactNode } from "react";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { keyCodeToKeyName } from "components/scrapEyeLayout/hotkeys/keyCodeConversion";
import { useTenant } from "hooks/settings";

export type HotkeyId =
  | "snapToCandidate"
  | "playPause"
  | "rewindLeft"
  | "forwardRight"
  | "stepBackward"
  | "stepForward"
  | "speedUp"
  | "speedDown"
  | "liftEvent"
  | "dropEvent"
  | "deleteEvent"
  | "submitEvents"
  | "reverseLiftDrop"
  | "editLastEvent"
  | "nextItem"
  | "previousItem"
  | "jumpForward"
  | "jumpBack"
  | "verifyWindow"
  | "editWindow"
  | "skipWindow"
  | "addFlag"
  | "areaMaterialMapping"
  | "cycleAngles"
  | "resetAnglesToAuto"
  | "cycleFootageLocation"
  | "unknownMaterial"
  | "changeMaterialMode"
  | "changeCoMaterialMode"
  | "changeAreaMode"
  | "toggleResolved"
  | `material_${number}`
  | `area_${string}`;

type HotkeyConfig = Readonly<{
  id: HotkeyId;
  defaultKey: string;
  displayName: string;
  category:
    "playback" | "events" | "materials" | "navigation" | "cameraSelection";
}>;

const DEFAULT_HOTKEYS: readonly HotkeyConfig[] = [
  {
    id: "playPause",
    defaultKey: "Space",
    displayName: "Space",
    category: "playback",
  },
  {
    id: "rewindLeft",
    defaultKey: "ArrowLeft",
    displayName: "←",
    category: "playback",
  },
  {
    id: "forwardRight",
    defaultKey: "ArrowRight",
    displayName: "→",
    category: "playback",
  },
  {
    id: "stepBackward",
    defaultKey: "BracketLeft",
    displayName: "[",
    category: "playback",
  },
  {
    id: "stepForward",
    defaultKey: "BracketRight",
    displayName: "]",
    category: "playback",
  },
  {
    id: "speedUp",
    defaultKey: "ArrowUp",
    displayName: "↑",
    category: "playback",
  },
  {
    id: "speedDown",
    defaultKey: "ArrowDown",
    displayName: "↓",
    category: "playback",
  },
  { id: "liftEvent", defaultKey: "KeyC", displayName: "C", category: "events" },
  { id: "dropEvent", defaultKey: "KeyV", displayName: "V", category: "events" },
  { id: "addFlag", defaultKey: "KeyX", displayName: "X", category: "events" },
  {
    id: "deleteEvent",
    defaultKey: "Backspace",
    displayName: "Backspace",
    category: "events",
  },
  {
    id: "submitEvents",
    defaultKey: "Period",
    displayName: ".",
    category: "events",
  },
  {
    id: "unknownMaterial",
    defaultKey: "KeyA",
    displayName: "A",
    category: "materials",
  },
  {
    id: "reverseLiftDrop",
    defaultKey: "KeyR",
    displayName: "R",
    category: "events",
  },
  {
    id: "editLastEvent",
    defaultKey: "KeyE",
    displayName: "E",
    category: "events",
  },
  {
    id: "snapToCandidate",
    defaultKey: "KeyA",
    displayName: "A",
    category: "events",
  },
  {
    id: "nextItem",
    defaultKey: "KeyN",
    displayName: "N",
    category: "navigation",
  },
  {
    id: "previousItem",
    defaultKey: "KeyP",
    displayName: "P",
    category: "navigation",
  },
  {
    id: "jumpForward",
    defaultKey: "KeyK",
    displayName: "K",
    category: "navigation",
  },
  {
    id: "jumpBack",
    defaultKey: "KeyJ",
    displayName: "J",
    category: "navigation",
  },
  {
    id: "verifyWindow",
    defaultKey: "KeyW",
    displayName: "W",
    category: "events",
  },
  {
    id: "editWindow",
    defaultKey: "KeyU",
    displayName: "U",
    category: "events",
  },
  {
    id: "skipWindow",
    defaultKey: "KeyS",
    displayName: "S",
    category: "events",
  },
  {
    id: "areaMaterialMapping",
    defaultKey: "KeyF",
    displayName: "F",
    category: "events",
  },
  {
    id: "cycleAngles",
    defaultKey: "KeyQ",
    displayName: "Q",
    category: "cameraSelection",
  },
  {
    id: "resetAnglesToAuto",
    defaultKey: "Digit0",
    displayName: "0",
    category: "cameraSelection",
  },
  {
    id: "cycleFootageLocation",
    defaultKey: "KeyL",
    displayName: "L",
    category: "cameraSelection",
  },
  {
    id: "changeMaterialMode",
    defaultKey: "KeyH",
    displayName: "H",
    category: "events",
  },
  {
    id: "changeCoMaterialMode",
    defaultKey: "KeyG",
    displayName: "G",
    category: "events",
  },
  {
    id: "changeAreaMode",
    defaultKey: "KeyB",
    displayName: "B",
    category: "events",
  },
  {
    id: "toggleResolved",
    defaultKey: "KeyM",
    displayName: "M",
    category: "events",
  },
] as const;

export const DEFAULT_MATERIAL_HOTKEYS = [
  "KeyS",
  "KeyD",
  "KeyF",
  "KeyG",

  "KeyQ",
  "KeyW",
  "KeyE",
  "KeyR",
  "KeyT",

  "KeyZ",
  "KeyX",
  "KeyC",
  "KeyV",
  "KeyB",

  "Digit1",
  "Digit2",
  "Digit3",
  "Digit4",
  "Digit5",

  "KeyH",
  "KeyJ",
  "KeyK",
  "KeyL",
  ";",

  "KeyY",
  "KeyU",
  "KeyI",
  "KeyO",
  "KeyP",

  "KeyN",
  "KeyM",
  ",",
  ".",
  "/",

  "Digit6",
  "Digit7",
  "Digit8",
  "Digit9",
  "Digit0",
] as const;

type HotkeyOverrides = Readonly<Record<string, string>>;

type HotkeyContextValue = Readonly<{
  getHotkey: (id: HotkeyId) => string;
  getDisplayKey: (id: HotkeyId) => string;
  setHotkey: (id: HotkeyId, key: string) => void;
  resetHotkey: (id: HotkeyId) => void;
  resetAllHotkeys: () => void;
  getMaterialHotkey: (index: number) => string;
  getDefaultKey: (id: HotkeyId) => string;
  getAreaHotkey: (areaName: string, defaultHotkey: string) => string;
  setAreaHotkey: (areaName: string, key: string) => void;
  resetAreaHotkey: (areaName: string) => void;
  setMaterialHotkey: (index: number, key: string) => void;
  resetMaterialHotkey: (index: number) => void;
}>;

const HotkeyContext = createContext<HotkeyContextValue | null>(null);

const getDisplayKeyFromCode = (code: string): string => {
  const displayMap: Record<string, string> = {
    Space: "Space",
    ArrowLeft: "←",
    ArrowRight: "→",
    ArrowUp: "↑",
    ArrowDown: "↓",
    Backspace: "Backspace",
    Period: ".",
  };

  if (displayMap[code]) {
    return displayMap[code];
  }

  if (code.startsWith("Key")) {
    return code.substring(3);
  }

  return code.toUpperCase();
};

const BASE_STORAGE_KEY = "scrap-eyes-hotkey-overrides";

export const HotkeyProvider = ({ children }: { children: ReactNode }) => {
  const tenant = useTenant();

  const legacyOverrides = useMemo<HotkeyOverrides>(() => {
    const raw = localStorage.getItem(BASE_STORAGE_KEY);
    if (!raw) return {};
    try {
      return JSON.parse(raw) as HotkeyOverrides;
    } catch {
      return {};
    }
  }, []);

  const [overrides, setOverrides] = useLocalStorageState(
    `${BASE_STORAGE_KEY}-${tenant}`,
    legacyOverrides
  );

  const getDefaultKey = useCallback((id: HotkeyId): string => {
    if (id.startsWith("material_")) {
      const index = parseInt(id.replace("material_", ""), 10);
      return DEFAULT_MATERIAL_HOTKEYS[index] ?? "";
    }
    const config = DEFAULT_HOTKEYS.find((h) => h.id === id);
    return config?.defaultKey ?? "";
  }, []);

  const getHotkey = useCallback(
    (id: HotkeyId): string => {
      // if hotkey is, for example, "KeyA", we want to return "A"
      return keyCodeToKeyName(overrides[id] ?? getDefaultKey(id));
    },
    [overrides, getDefaultKey]
  );

  const getDisplayKey = useCallback(
    (id: HotkeyId): string => {
      const key = getHotkey(id);
      return getDisplayKeyFromCode(key);
    },
    [getHotkey]
  );

  const setHotkey = useCallback(
    (id: HotkeyId, key: string) => {
      setOverrides((prev) => ({ ...prev, [id]: key }));
    },
    [setOverrides]
  );

  const resetHotkey = useCallback(
    (id: HotkeyId) => {
      setOverrides((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([k]) => k !== id))
      );
    },
    [setOverrides]
  );

  const resetAllHotkeys = useCallback(() => {
    setOverrides({});
  }, [setOverrides]);

  // Area hotkey functions
  const getAreaHotkey = useCallback(
    (areaName: string, defaultHotkey: string): string => {
      const id = `area_${areaName}` as HotkeyId;
      return overrides[id] ?? defaultHotkey;
    },
    [overrides]
  );

  const setAreaHotkey = useCallback(
    (areaName: string, key: string) => {
      const id = `area_${areaName}` as HotkeyId;
      setOverrides((prev) => ({ ...prev, [id]: key }));
    },
    [setOverrides]
  );

  const resetAreaHotkey = useCallback(
    (areaName: string) => {
      const id = `area_${areaName}` as HotkeyId;
      setOverrides((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([k]) => k !== id))
      );
    },
    [setOverrides]
  );

  // Material hotkey functions
  // Note: index -1 is used for the "unknown" material
  const getMaterialHotkey = useCallback(
    (index: number): string => {
      if (index === -1) {
        return overrides.unknownMaterial ?? getDefaultKey("unknownMaterial");
      }
      const id = `material_${index}` as HotkeyId;
      return overrides[id] ?? DEFAULT_MATERIAL_HOTKEYS[index] ?? "";
    },
    [overrides, getDefaultKey]
  );

  const setMaterialHotkey = useCallback(
    (index: number, key: string) => {
      if (index === -1) {
        // Unknown material uses the unknownMaterial hotkey
        setHotkey("unknownMaterial", key);
        return;
      }
      const id = `material_${index}` as HotkeyId;
      setOverrides((prev) => ({ ...prev, [id]: key }));
    },
    [setHotkey, setOverrides]
  );

  const resetMaterialHotkey = useCallback(
    (index: number) => {
      if (index === -1) {
        // Unknown material uses the unknownMaterial hotkey
        resetHotkey("unknownMaterial");
        return;
      }
      const id = `material_${index}` as HotkeyId;
      setOverrides((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([k]) => k !== id))
      );
    },
    [resetHotkey, setOverrides]
  );

  const value = useMemo<HotkeyContextValue>(
    () => ({
      getHotkey,
      getDisplayKey,
      setHotkey,
      resetHotkey,
      resetAllHotkeys,
      getMaterialHotkey,
      getDefaultKey,
      getAreaHotkey,
      setAreaHotkey,
      resetAreaHotkey,
      setMaterialHotkey,
      resetMaterialHotkey,
    }),
    [
      getHotkey,
      getDisplayKey,
      setHotkey,
      resetHotkey,
      resetAllHotkeys,
      getMaterialHotkey,
      getDefaultKey,
      getAreaHotkey,
      setAreaHotkey,
      resetAreaHotkey,
      setMaterialHotkey,
      resetMaterialHotkey,
    ]
  );

  return <HotkeyContext value={value}>{children}</HotkeyContext>;
};

export const useHotkeyContext = (): HotkeyContextValue => {
  const context = use(HotkeyContext);
  if (!context) {
    throw new Error("useHotkeyContext must be used within HotkeyProvider");
  }
  return context;
};
